/**
 * HomePage: golfer-first marketing, WITB ("What's in your bag?")
 * positioning.
 *
 * Strategy: lead with the golfer outcome (a digital, valued bag).
 * The bag is built by photographing the clubs, confirming the heads,
 * and capturing serials. Snap your bag is an iOS / Android app feature
 * (the web build ships with VITE_BAG_SCAN_ENABLED off), so copy that
 * promises photos says "in the app"; on the web clubs are added by search. A receipt attaches to a club already listed
 * and raises trust only when the head agrees. Single primary CTA
 * throughout. The pro narrative is a single band → /pro. No real
 * brand names in the visible copy or mockups.
 *
 * PRESERVED FROM PRIOR VERSIONS (do not modify):
 *   - Auth callback handler in the useEffect (magic-link / OAuth on /)
 *   - useTheme() detection
 *   - customer auth-state read from useAuthStore
 *   - Existing Vite/Router/icon imports
 *
 * Routes preserved:
 *   /customer/register   golfer signup (primary)
 *   /customer/login      golfer signin
 *   /pro                 pro marketing
 */

import { useEffect, useState } from 'react';
import {
  Activity,
  ArrowLeftRight,
  ArrowRight,
  ChevronDown,
  CheckCircle2,
  Compass,
  Fingerprint,
  Flame,
  Mail,
  MessageSquare,
  PenLine,
  ScanLine,
  Search,
  ShieldCheck,
  ShoppingBag,
  Target,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthStore } from '../store/authStore';
import { goToCore } from '../lib/goToCore';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { GolfAuthLoadingScreen, PricingMatrix, type PricingTier, type PricingRow } from '@golfauth/ui';
import { useRedirectIfCoreHandoff } from '../hooks/useRedirectIfCoreHandoff';
import AudienceChooser from '../components/marketing/AudienceChooser';
import MarketingField from '../components/marketing/MarketingField';
import MarketingNav from '../components/marketing/MarketingNav';
import HeroLoop from '../components/marketing/HeroLoop';
import StoreBadges from '../components/marketing/StoreBadges';
import '../styles/marketingRedesign.css';

// `Capacitor.isNativePlatform()` is a static check. Value is constant per
// session and returns false in browsers, so safe to read at module scope.
// True only when this SPA is running inside the GolfAuth Pro Capacitor
// wrap (com.golfauth.pro). That wrap is Pro-exclusive: it must never
// show the golfer-first marketing homepage. See the native-platform
// branch in the redirect effect below.
const IS_NATIVE = Capacitor.isNativePlatform();
import { useHeadMeta } from '../hooks/useHeadMeta';

// ─────────────────────────────────────────────────────────────────────
// CTA targets, preserved from previous round
// ─────────────────────────────────────────────────────────────────────
const PRIMARY_CTA = '/customer/register?source=homepage&intent=valuation';
const PRIMARY_CTA_LABEL = 'Build my bag, free';
const FIRST_CLUB_CTA = '/customer/register?source=homepage&intent=first-club';
const ROAST_CTA = '/customer/register?source=homepage&intent=roast';
// Shared risk-reversal microcopy shown under primary CTAs. Reflects the
// real golfer journey (see supabase/migrations/20260521210000_auto_start_
// golfer_trial.sql): every signup gets 60 days of full Premium with no
// card, then auto-drops to Free forever unless they choose to pay. Both
// hooks are true and complementary, so the copy states both.
// Premium-redesign copy (approved prototype): states the trial, the
// no-card promise, and that the golfer then chooses their ongoing tier.
const CTA_REASSURANCE = '2 months of Premium free · No card · then choose Free, Premium, or Platinum';

// Theme-agnostic "always dark navy" hex from the brand guidelines.
// Used on the hero, how-it-works, pro band, and final CTA bands so
// they read as dark even in light mode.
const NAVY = '#00112B';

// ─────────────────────────────────────────────────────────────────────
// Pricing matrix: golfer tiers (D9 pricing v2, June 2026).
// Pro tiers live on ProPage.tsx so the audience-aware separation holds:
// golfers visiting / see golfer pricing, pros visiting /pro see pro
// pricing. Per-feature cap values must stay in sync with
// apps/core/.../src/lib/ai/tierLimits.ts.
// ─────────────────────────────────────────────────────────────────────
const GOLFER_PRICING_TIERS: PricingTier[] = [
  {
    name: 'Free',
    tagline: 'Build your bag. Try the platform.',
    ctaLabel: 'Start free',
    ctaHref: '/customer/register?source=homepage_pricing&tier=free',
    ctaVariant: 'secondary',
  },
  {
    name: 'Premium',
    tagline: 'Full Caddie toolkit + smarter answers.',
    monthlyPrice: 'A$1.99',
    annualPrice: 'A$19.99',
    annualSavings: 'Save 16%',
    highlight: true,
    badge: 'Most popular',
    ctaLabel: 'Start 2-month free trial',
    ctaHref: '/customer/register?source=homepage_pricing&tier=premium',
  },
  {
    name: 'Platinum',
    tagline: 'Maximum allowance for daily players.',
    monthlyPrice: 'A$4.99',
    annualPrice: 'A$49.99',
    annualSavings: 'Save 16%',
    ctaLabel: 'Go Platinum',
    ctaHref: '/customer/register?source=homepage_pricing&tier=platinum',
  },
];

const GOLFER_PRICING_ROWS: PricingRow[] = [
  { label: 'Bag & ownership', isSection: true },
  { label: 'Virtual bag (view + add clubs)', values: [true, true, true] },
  {
    label: 'Receipt upload + verification',
    detail: 'Attach a receipt to a club already in the bag. A matching head raises it to Receipt-Verified.',
    values: [true, true, true],
  },
  {
    label: 'Bag valuation',
    detail: 'Live eBay comps, depreciation curve, per-club breakdown.',
    values: [false, true, true],
  },
  {
    label: 'Sales listings',
    detail: 'Active marketplace listings you can keep live at once.',
    values: [false, 'Up to 3', 'Unlimited'],
  },
  { label: 'Lost & Found alerts', values: [true, true, true] },
  { label: 'Receipt forwarding email', values: [true, true, true] },

  { label: 'AI Caddie', isSection: true },
  {
    label: 'AI Caddie chat',
    detail:
      'Six personalities. Knows your bag, course data, and live tournaments. Remaining allowance is always visible in-app.',
    values: ['10 / mo teaser', '20 / mo', '50 / mo'],
  },
  {
    label: 'Deeper AI answers',
    detail:
      'Richer analysis on strategy, gapping, and gear — not just shorter teaser replies.',
    values: ['Teaser', 'Full depth', 'Priority depth'],
  },
  { label: 'Gap analysis', values: [false, '5 / mo', '20 / mo'] },
  { label: 'Course recommendation', values: [false, '5 / mo', '20 / mo'] },
  { label: 'Practice plan', values: [false, '5 / mo', '20 / mo'] },

  { label: 'Reports & insights', isSection: true },
  {
    label: 'Bag report',
    detail: 'Full bag teardown: gaps, strengths, upgrade ideas.',
    values: ['1 / yr', '5 / mo', '20 / mo'],
  },
  {
    label: 'Roast my bag',
    detail: 'Caddie tells it like it is.',
    values: ['3 / yr', '5 / mo', '20 / mo'],
  },
  {
    label: 'Listing description AI',
    values: ['1 / yr', '5 / mo', '20 / mo'],
  },
];

// ─────────────────────────────────────────────────────────────────────
// Hero bag mockup: generic equipment labels only. NO REAL BRAND NAMES.
// Total auto-derives from these values, so the headline always matches.
// ─────────────────────────────────────────────────────────────────────
const BAG_CLUBS: Array<{ type: string; specs: string; value: number; trust: string }> = [
  { type: 'Driver', specs: '10.5° · Stiff', value: 640, trust: 'Registered' },
  { type: '3-Wood', specs: '15° · Stiff', value: 420, trust: 'Registered' },
  { type: '4-Iron to 9-Iron', specs: 'Forged · Regular', value: 1200, trust: 'Registered' },
  { type: 'Pitching Wedge', specs: '46° · Stiff', value: 190, trust: 'Registered' },
  { type: 'Sand Wedge', specs: '56° · Stiff', value: 190, trust: 'Registered' },
  { type: 'Putter', specs: '34" · Blade', value: 495, trust: 'Verified' },
];
const TOTAL_BAG_VALUE = BAG_CLUBS.reduce((s, c) => s + c.value, 0);

// ─────────────────────────────────────────────────────────────────────
// Feature showcase: the full golfer feature set, grouped into four
// benefit themes. Every item maps to a real shipped feature (see
// apps/core AI routes, valuation, club-transfer lifecycle, listings,
// verification tiers). Copy is benefit-led, not feature-name-led.
// ─────────────────────────────────────────────────────────────────────
const FEATURE_GROUPS: Array<{
  eyebrow: string;
  title: string;
  image?: string;
  cards: Array<{ icon: typeof TrendingUp; title: string; body: string }>;
}> = [
  {
    eyebrow: 'Know what you own',
    title: 'Your whole bag, captured and valued.',
    image: '/marketing/feat-value.jpg',
    cards: [
      {
        icon: ScanLine,
        title: 'Photograph the bag you play',
        body: 'In the iPhone or Android app, snap the clubs, confirm the heads, and they are in your bag. On the web, search for each club by name. Photograph the serial stamp or type it. Shaft and grip can wait. When a receipt matches the head, that club moves from registered to Receipt-Verified.',
      },
      {
        icon: TrendingUp,
        title: 'Real market values, on request',
        body: 'Ask for a valuation and every club is priced from live secondary-market sales, not guesswork. Know exactly what your driver, irons, wedges, and putter are worth to sell, insure, or trade in.',
      },
      {
        icon: Activity,
        title: 'See the impact before you buy',
        body: 'Thinking about a new shaft, head, or wedge? Equipment impact shows how the change would affect your gapping and setup before you spend a cent.',
      },
    ],
  },
  {
    eyebrow: 'Play better',
    title: 'An AI caddie that actually knows your clubs.',
    image: '/marketing/feat-caddie.jpg',
    cards: [
      {
        icon: MessageSquare,
        title: 'A caddie with real personality',
        body: 'Course strategy and club selection grounded in the exact clubs you own, your course data, and live tournaments. Try a limited monthly chat on Free; upgrade for more messages and deeper answers.',
      },
      {
        icon: Target,
        title: 'Gap analysis & practice plans',
        body: 'Find the distance gaps hiding in your bag, then get a practice plan built to close them.',
      },
      {
        icon: Compass,
        title: 'Course game plans',
        body: 'Give the caddie the course and conditions and get a plan for playing it with the clubs you actually carry.',
      },
    ],
  },
  {
    eyebrow: 'Own it with proof',
    title: 'Trust that travels with the club.',
    image: '/marketing/feat-verify.jpg',
    cards: [
      {
        icon: ShieldCheck,
        title: 'Verified ownership, four tiers',
        body: 'Every club carries a record: self-registered, receipt-verified, pro-verified, or GolfAuth Certified. A matching receipt is what moves a club up from registered.',
      },
      {
        icon: ArrowLeftRight,
        title: 'Transfer provenance in one tap',
        body: 'Selling or handing a club on? Move the digital twin, full ownership history included, so the next owner inherits the trust you built.',
      },
      {
        icon: Search,
        title: 'Lost & Found alerts',
        body: 'Flag a club lost or stolen and the network watches for it. If anyone tries to register or transfer that serial, you know.',
      },
      {
        icon: Fingerprint,
        title: 'Anti-counterfeit by network',
        body: 'Every receipt sharpens fraud detection and serials are cross-checked across the whole network, so fakes get harder to pass off every day.',
      },
    ],
  },
  {
    eyebrow: 'Buy, sell & get fitted',
    title: 'A marketplace and a fitting bench, connected to your bag.',
    image: '/marketing/feat-market.jpg',
    cards: [
      {
        icon: ShoppingBag,
        title: 'A local marketplace built on trust',
        body: 'Buy and sell with golfers near you in a marketplace where provenance is visible, so both sides know exactly what is real before money changes hands.',
      },
      {
        icon: PenLine,
        title: 'Listings that write themselves',
        body: 'Create a listing in seconds. The AI drafts the description straight from your club’s specs and condition, ready to post locally.',
      },
      {
        icon: Wrench,
        title: 'Straight to a fitting, delivered',
        body: 'Connect to a GolfAuth Pro, view your fitting configurations, and buy the build with your bag and history already in the room. It arrives Pro-Verified, full spec and provenance attached.',
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────
// How it works: 3 steps
// ─────────────────────────────────────────────────────────────────────
const HOW_STEPS = [
  {
    icon: ScanLine,
    title: 'Snap your bag',
    visual: 'Up to 7 photos · iOS + Android app',
    caption:
      'In the app, we read brand, model, and loft. You confirm anything missing, then add them. They start as registered.',
  },
  {
    icon: Fingerprint,
    title: 'Capture each serial',
    visual: 'Stamp fills the frame',
    caption:
      'Type it, or photograph the plate close up. One question per club. An iron set is one question.',
  },
  {
    icon: Mail,
    title: 'Attach a receipt when you have one',
    visual: 'You choose the club',
    caption:
      'Forward the email or upload the photo. When the head agrees, that club becomes Receipt-Verified.',
  },
];

const VERIFICATION_TRUST_CHIPS = [
  'Network learning from every receipt',
  'Serials cross-checked network-wide',
  'Public transparency reports',
];

// The homepage's "Built for the whole golf ecosystem" audience grid was
// removed here (D-onwards): the About page carries a fuller version of
// that narrative (Who-it-serves grid + Principles), so keeping it on the
// homepage duplicated content and pushed the golfer's path to pricing +
// signup further down. Section 6.5 now condenses to one line + an About
// link. See apps/fitting/src/pages/AboutPage.tsx for the full story.

// ─────────────────────────────────────────────────────────────────────
// FAQ: verbatim copy from spec. Same array drives both the visible
// accordion AND the FAQPage JSON-LD payload below.
// ─────────────────────────────────────────────────────────────────────
const FAQ_ITEMS: Array<{ q: string; a: string }> = [
  {
    q: 'Do I have to type in every club manually?',
    a: 'No. In the GolfAuth app for iPhone and Android, Snap your bag: photograph the clubs, confirm brand, model, and loft, and add them. On the web, search by brand and model and pick the match. Then capture each serial by typing it or photographing the stamp close up. If you have the purchase email, forward it, or upload a photo of a paper receipt, and attach each line to the club it belongs to. Voice search is still there when you are adding one club. Say "driver, 10.5 degrees, stiff shaft" and confirm the model. Shaft and grip can be filled in on the club later.',
  },
  {
    q: 'Is my serial number safe?',
    a: 'Yes. Serial numbers are protected by a GolfAuth Serial (GA Serial) - a tokenised reference that the platform uses for verification and cross-referencing. Your real serial is only visible to you, never displayed publicly, and never shared with retailers, brands, or third parties without your explicit consent. The GA Serial behaves like a payment-card tokenisation - it lets the system verify ownership records without exposing the underlying serial to anyone else.',
  },
  {
    q: 'What if I bought my equipment second-hand?',
    a: 'You can still register clubs without a receipt. They start with a baseline trust score that can be increased through verified transfers from previous owners, professional fitter verification, or supporting documentation like a bill of sale. This matters because the secondary golf equipment market is where most counterfeit risk lives - and most golfers selling or buying second-hand have no way to verify what they’re actually getting. GolfAuth exists to give second-hand buyers a credible way to confirm what they own, and second-hand sellers a credible way to prove it.',
  },
  {
    q: 'Does it work outside Australia?',
    a: 'Yes. We process receipts from major retailers across Australia, the US, the UK, and Europe - and the system supports a growing list of other markets. Currency is auto-detected from the receipt, so your bag valuations reflect the local market where you bought or where you’d resell.',
  },
  {
    q: 'How do you protect against fake receipts?',
    a: 'Every receipt is processed through structural validation - we verify sender domains match the claimed retailer, check that order details are internally consistent (totals, line items, dates), and cross-reference serial numbers across the network to detect duplicates. As more receipts flow through GolfAuth, the system gets better at detecting fabricated receipts and we’re building direct partnerships with major retailers for real-time order verification.',
  },
  {
    q: 'What do the golfer plans cost?',
    a: 'Free is free forever. Premium is A$1.99/month or A$19.99/year, with a 2-month free trial and no card up front. Platinum is A$4.99/month or A$49.99/year for higher Caddie and report allowances. The App Store and Google Play show the price in your local currency. The pricing table above lists what each plan includes.',
  },
  {
    q: 'What happens if my equipment is stolen or sold?',
    a: 'When you transfer a club through GolfAuth - to a buyer, to a family member, or back to a retailer - the digital twin moves with it, including the full ownership history. If equipment is stolen, you can flag it as “Lost or Stolen” on your bag, and the network is alerted if anyone attempts to register or transfer that serial number. Insurance claims become straightforward because you have a documented record of what you owned, what it was worth, and the verification trail behind it.',
  },
  {
    q: 'Why should I build a verified record of my bag?',
    a: 'Three reasons. First, you’ll always know what your gear is actually worth - for selling, insuring, or just feeling good about what you own. Second, an AI caddie that knows your specific clubs gives meaningfully better advice than generic apps. Third, you’re protected when you sell, transfer, or trade in - and so is the next owner. WITB isn’t about verification for its own sake. It’s about turning the bag you’ve spent thousands building into something you can actually use as data.',
  },
];

export default function HomePage() {
  // ─── Wiring preserved verbatim ────────────────────────────────────
  // Core handoff: when goToFitting() drops the golfer here with a
  // `?from=core`/`?token=` URL OR sessionStorage flag (set by
  // supabaseAuthReady after the URL strip), bounce to the dashboard +
  // suppress this marketing UI in the meantime. User-reported
  // 2026-06-25: tapping "Fittings" in Core briefly showed HomePage.
  // Hook called here so it stays in a fixed slot in hook order; the
  // early-return based on its result lives after every other hook in
  // this component so Rules-of-Hooks isn't violated.
  const handoffPending = useRedirectIfCoreHandoff('/golfer/dashboard');
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { customer, professional, isInitialized } = useAuthStore();

  // PWA-launch redirect: if the user opens the installed consumer PWA
  // and lands here (iOS Safari pre-16.4 quirk, or Add-to-Home-Screen
  // from / on any platform), send them straight to Core rather than
  // staring at marketing. Previously bounced via /golfer →
  // GolferLandingPage → goToCore, but that landing page was removed
  // 2026-07-02; call goToCore() directly here instead.
  // No-op for normal browser sessions.
  //
  // Pros must NEVER take this path: a Pro who lands on `/` (or whose
  // Pro PWA opens `/`) would otherwise be dumped into Core. Wait for
  // auth hydrate; Pros are handled by the /pro/admin redirect below.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isInitialized) return;
    if (professional) return;
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      window.matchMedia?.('(display-mode: fullscreen)')?.matches ||
      // iOS Safari Home Screen launch (Apple pre-standard property).
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    if (isStandalone) {
      void goToCore();
    }
  }, [isInitialized, professional]);

  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [mounted, setMounted] = useState(false);

  useDocumentTitle("GolfAuth | WITB — What's in your bag?");
  useHeadMeta({
    description:
      'Photograph the clubs in your bag with the iPhone or Android app, confirm what we read, and attach a receipt when you have one. Live valuations, an AI caddie that knows your bag, and verified ownership. Free, or from A$1.99/mo.',
    canonical: 'https://golfauth.com/',
    og: {
      title: "GolfAuth — What's in your bag?",
      description:
        "Your bag, valued from live sales data. An AI caddie that knows every club you carry. Proof it's yours when you sell.",
      type: 'website',
      url: 'https://golfauth.com/',
      image: 'https://golfauth.com/og/home.png',
    },
    twitter: {
      card: 'summary_large_image',
      title: "GolfAuth — What's in your bag?",
      description:
        "Your bag, valued from live sales data. An AI caddie that knows every club you carry. Proof it's yours when you sell.",
      image: 'https://golfauth.com/og/home.png',
    },
    // AEO Phase 1: four structured-data payloads on the homepage:
    //   1. Organization (enriched): knowledge-graph entity for GolfAuth,
    //      now carrying foundingDate, areaServed, knowsAbout, and
    //      contactPoint so AI engines have richer attribution data.
    //   2. WebSite: exposes the canonical URL + SearchAction so AI
    //      engines can route searches to the right place.
    //   3. Service: GolfAuth Core consumer subscription, with Premium
    //      and Platinum tier offers exposed as PriceSpecifications.
    //      Drives "best app to value your golf bag" / "subscribe to
    //      GolfAuth" style citations.
    //   4. FAQPage: drives Google rich-snippet eligibility AND gives
    //      AI engines self-contained Q&A facts to lift verbatim.
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': 'https://golfauth.com#organization',
        name: 'GolfAuth',
        legalName: 'GolfAuth',
        url: 'https://golfauth.com',
        logo: 'https://golfauth.com/logos/golfauth-light-1280w.webp',
        description:
          'WITB — every club, known and yours. Photograph the clubs in your bag with the iPhone or Android app, confirm what we read, and attach a receipt when you have one. GolfAuth values every club from live sales data and gives you an AI caddie that knows exactly what you carry.',
        slogan: "What's in your bag?",
        foundingDate: '2025',
        areaServed: { '@type': 'Place', name: 'Worldwide' },
        knowsAbout: [
          'golf equipment verification',
          'golf club provenance',
          'verified ownership',
          'chain of custody',
          'counterfeit detection',
          'custom club fitting',
          'pro shop operations',
          'lost and found audit trail',
        ],
        sameAs: [],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          url: 'https://golfauth.com/contact',
          availableLanguage: ['en'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': 'https://golfauth.com#website',
        url: 'https://golfauth.com',
        name: 'GolfAuth',
        publisher: { '@id': 'https://golfauth.com#organization' },
        inLanguage: 'en',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': 'https://golfauth.com#golfauth-core-service',
        name: 'GolfAuth Core',
        description:
          'The golfer-facing application for the GolfAuth verification network. Includes a verified digital bag, AI Caddie chat, equipment valuations, bag depreciation history, sales listings, and one-tap chain-of-custody transfers.',
        provider: { '@id': 'https://golfauth.com#organization' },
        serviceType: 'Golf equipment verification and digital bag management',
        areaServed: { '@type': 'Place', name: 'Worldwide' },
        offers: [
          {
            '@type': 'Offer',
            name: 'Free',
            description:
              'Virtual bag, add up to 14 clubs, receipt upload + forwarding (attach a receipt to a club already in the bag; a matching head raises it to Receipt-Verified), Lost & Found alerts, limited AI Caddie chat (10 / month teaser), and yearly bag report / roast / listing description samples.',
            price: '0',
            priceCurrency: 'AUD',
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: 'Premium',
            description:
              'Everything in Free plus full AI Caddie chat (20 / month), deeper answers, bag valuations, sales listings, and AI reports: gap analysis, course recommendation, practice plan, bag report and roast at 5 / month each. Two-month free trial.',
            price: '19.99',
            priceCurrency: 'AUD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '1.99',
              priceCurrency: 'AUD',
              billingDuration: 'P1M',
              unitText: 'MONTH',
            },
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: 'Platinum',
            description:
              'Everything in Premium with AI Caddie chat lifted to 50 / month, AI reports lifted to 20 / month each, and priority depth on complex strategy and gear questions.',
            price: '49.99',
            priceCurrency: 'AUD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '4.99',
              priceCurrency: 'AUD',
              billingDuration: 'P1M',
              unitText: 'MONTH',
            },
            availability: 'https://schema.org/InStock',
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQ_ITEMS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
      // SoftwareApplication: Google's canonical rich-result type for
      // apps. Reuses the same three tier offers already exposed on the
      // Service block, so the pricing surfaces both as "Service offers"
      // (for lists-with-price citations) and as "app offers" (for the
      // "app pricing" panel in Search). No invented ratings; Google
      // will surface aggregateRating only when we can back it with real
      // review data.
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        '@id': 'https://golfauth.com#software-application',
        name: 'GolfAuth',
        url: 'https://golfauth.com',
        description:
          'Virtual golf bag with verified digital club identity, AI Caddie chat, valuations, and chain-of-custody transfers. Free or paid from AUD 1.99 / month.',
        applicationCategory: 'LifestyleApplication',
        operatingSystem: 'iOS, Web',
        publisher: { '@id': 'https://golfauth.com#organization' },
        offers: [
          {
            '@type': 'Offer',
            name: 'Free',
            price: '0',
            priceCurrency: 'AUD',
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: 'Premium',
            price: '19.99',
            priceCurrency: 'AUD',
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: 'Platinum',
            price: '49.99',
            priceCurrency: 'AUD',
            availability: 'https://schema.org/InStock',
          },
        ],
      },
    ],
  });

  // Auth callback handler, preserved verbatim. Magic-link / OAuth
  // callbacks may land on `/` with the code in the query or hash;
  // bounce them through the proper callback route immediately.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const hashParams = new URLSearchParams((location.hash || '').substring(1));
    const hasAuthTokens = hashParams.get('access_token') || hashParams.get('token_hash');
    if (code) {
      navigate(`/auth/callback${location.search}`, { replace: true });
    } else if (hasAuthTokens) {
      navigate(`/auth/callback${location.hash || ''}`, { replace: true });
    }
  }, [location.search, location.hash, navigate]);

  // Pro shortcut + native-app entry redirect.
  //
  // Web: a logged-in professional landing on `/` is almost certainly
  // here to do work, not to read golfer marketing, so bounce them to
  // /pro/admin (same default LoginPage uses after a fresh sign-in).
  // Customers and unauthenticated web visitors continue to see the
  // homepage as before.
  //
  // Native (Pro Capacitor app, com.golfauth.pro): this wrap is
  // Pro-exclusive: the golfer-first homepage must never appear.
  // Send authenticated pros to /pro/admin, everyone else to
  // /login. Golfers should not have this app installed; if they do,
  // they'll hit /login and either pass through (rare) or bounce off
  // off; they're served by the golfer Capacitor wrap (com.golfauth.core)
  // and the web at app.golfauth.com.
  //
  // ProtectedRoute on /pro/admin still enforces approval +
  // subscription gates downstream, so this redirect doesn't bypass
  // any access control.
  useEffect(() => {
    if (!isInitialized) return;
    if (IS_NATIVE) {
      navigate(professional ? '/pro/admin' : '/pro/login', { replace: true });
      return;
    }
    if (professional) {
      navigate('/pro/admin', { replace: true });
    }
  }, [isInitialized, professional, navigate]);

  // Trigger entrance animation on mount (CSS-only, see styles below).
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  // resolvedTheme retained so this hook keeps a dependency on it for
  // any future theme-conditional rendering, and so the import isn't
  // dropped by the type-checker as unused.
  void resolvedTheme;

  // Native (Pro Capacitor app): the redirect effect above will navigate
  // us off this route the moment auth initialises. Skip painting the
  // golfer marketing content entirely so there's no flash of "Intelligent
  // Golf Equipment / golfer-first" content while we wait. Renders nothing
  // for a few ms, then the LoginPage / SelectGolferPage takes over.
  if (IS_NATIVE) {
    return null;
  }

  // Core handoff in flight (see useRedirectIfCoreHandoff above):
  // suppress the marketing UI and render a loading screen while the
  // navigate(target) call from the hook is processing.
  if (handoffPending) {
    return <GolfAuthLoadingScreen />;
  }

  return (
    <div className="mkt mkt--golfer" style={{ backgroundColor: NAVY }}>
      {/* First-visit audience splash. Anonymous web visitors only (auth
          resolved, not a signed-in golfer/pro). Routes pros to /pro; the
          overlay itself no-ops for anyone who already chose. Native +
          handoff already returned above, so this is web-only here. */}
      {isInitialized && !customer && !professional && <AudienceChooser />}

      {/* Ambient topographic canvas + scroll-reveal driver. Renders
          nothing (and starts no effects) under prefers-reduced-motion. */}
      <MarketingField />

      <div className="content">
        {/* The audience split lives in the nav ("For professionals") and
            the golfer/pro panels further down, so the old top-strip
            cross-promo banner was removed here. */}

        {/* ═════════════ NAV ═════════════ */}
        <MarketingNav
          links={[
            { label: 'For golfers', href: '#features' },
            { label: 'For professionals', to: '/pro', kind: 'pill' },
            { label: 'Pricing', href: '#pricing' },
          ]}
          cta={
            customer
              ? { label: 'Open your bag', onClick: goToCore }
              : { label: 'Start free', to: PRIMARY_CTA }
          }
        />

        {/* ═════════════ HERO ═════════════ */}
        <header className="hero">
          <div className="hero-media" aria-hidden="true">
            <HeroLoop />
          </div>
          <div className="wrap hero-grid">
            <div className={`reveal ${mounted ? 'in' : ''}`}>
              <p className="eyebrow">WITB, answered</p>
              <h1 className="display">
                What's in<br />
                <span className="line2">your bag?</span>
              </h1>
              <p className="lede">
                Photograph the clubs in your bag with the GolfAuth app. Confirm
                what we read, and they are yours. Attach a receipt when you have one. An AI caddie knows
                exactly what you carry.
              </p>
              <div className="cta-row">
                {customer ? (
                  <button type="button" className="btn big" onClick={goToCore}>
                    Open your bag
                    <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
                  </button>
                ) : (
                  <Link to={PRIMARY_CTA} className="btn big">
                    {PRIMARY_CTA_LABEL}
                    <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
                  </Link>
                )}
              </div>
              {!customer && (
                <p className="reassure">
                  <b>2 months of Premium free</b> · No card · then choose Free,
                  Premium, or Platinum
                </p>
              )}
              <StoreBadges />
              <div className="trust">
                <div className="ti">
                  <span className="k">${TOTAL_BAG_VALUE.toLocaleString()}</span>
                  <span className="l">Avg bag, valued</span>
                </div>
                <div className="ti">
                  <span className="k">4</span>
                  <span className="l">Tiers of provenance</span>
                </div>
                <div className="ti">
                  <span className="k">On request</span>
                  <span className="l">Live comps + curves</span>
                </div>
              </div>
            </div>

            <div className={`stage reveal ${mounted ? 'in' : ''}`}>
              <div className="glow" aria-hidden="true" />
              <span className="floaty f1" aria-hidden="true">GA-Serial · Tokenised</span>
              <span className="floaty f2" aria-hidden="true">▲ Valued on request</span>
              <div
                className="card"
                role="img"
                aria-label={`A digital golf bag. Most clubs registered, the putter verified, values totalling ${TOTAL_BAG_VALUE.toLocaleString()} dollars`}
              >
                <div className="chead">
                  <span className="clabel">Your bag</span>
                  <span className="ctotal">
                    <span className="ct-l">Total value</span>
                    <br />
                    <span className="ct-v">
                      ${TOTAL_BAG_VALUE.toLocaleString()}
                      <span style={{ fontSize: '.7rem', color: 'var(--muted)' }}> AUD</span>
                    </span>
                  </span>
                </div>
                {BAG_CLUBS.map((club) => (
                  <div className="club" key={club.type}>
                    <div className="cn">
                      <div className="t">{club.type}</div>
                      <div className="s">{club.specs}</div>
                    </div>
                    <span className="chip">
                      <CheckCircle2 aria-hidden="true" />
                      {club.trust}
                    </span>
                    <span className="cv">${club.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </header>

        <div className="wrap"><div className="rule" /></div>

        {/* ═════════════ HOW IT WORKS (HOW_STEPS) ═════════════ */}
        <section className="band band-continue">
          <div className="wrap">
            <div className="sec-head reveal" style={{ marginBottom: '40px' }}>
              <div>
                <p className="eyebrow">Build your digital bag</p>
                <h2>
                  Photograph the bag.<br />Confirm the clubs.
                </h2>
              </div>
            </div>
            <div className="caps reveal">
              {HOW_STEPS.map((step) => {
                const Icon = step.icon;
                return (
                  <div className="cap" key={step.title}>
                    <span className="ci"><Icon aria-hidden="true" /></span>
                    <div>
                      <div className="ct">{step.title}</div>
                      <div className="cd mono" style={{ color: 'var(--cyan)', margin: '6px 0' }}>
                        {step.visual}
                      </div>
                      <div className="cd">{step.caption}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="cta-row reveal" style={{ marginTop: '36px' }}>
              {customer ? (
                <button type="button" className="btn big" onClick={goToCore}>
                  Open your bag
                  <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
                </button>
              ) : (
                <Link to={FIRST_CLUB_CTA} className="btn big">
                  {PRIMARY_CTA_LABEL}
                  <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
                </Link>
              )}
            </div>
            {!customer && <p className="reassure">{CTA_REASSURANCE}</p>}
          </div>
        </section>

        {/* ═════════════ MANIFESTO ═════════════ */}
        <section className="band mband">
          <div className="m-bg" aria-hidden="true">
            <video
              className="media-cover"
              autoPlay
              muted
              loop
              playsInline
              poster="/marketing/manifesto2-poster.jpg"
            >
              <source src="/marketing/manifesto2.webm" type="video/webm" />
              <source src="/marketing/manifesto2.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="wrap reveal">
            <p className="eyebrow">Why GolfAuth exists</p>
            <p className="manifesto">
              Golf's equipment has always run on guesswork.{' '}
              <span className="dim">Is it real? What is it worth? Does it suit you?</span>{' '}
              <span className="hot">We connect every club to the answer.</span>
            </p>
          </div>
        </section>

        {/* ═════════════ FEATURE ACTS (FEATURE_GROUPS) ═════════════ */}
        <section className="band band-continue" id="features">
          <div className="wrap">
            <div className="acts">
              {[FEATURE_GROUPS[0], FEATURE_GROUPS[1]].map((group) => (
                <div className="act reveal" key={group.eyebrow}>
                  <div className="ahead">
                    <p className="eyebrow">{group.eyebrow}</p>
                    <h2>{group.title}</h2>
                  </div>
                  {group.image && (
                    <div className="act-media">
                      <img
                        src={group.image}
                        alt=""
                        className="media-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="caps">
                    {group.cards.map((card) => {
                      const Icon = card.icon;
                      return (
                        <div className="cap" key={card.title}>
                          <span className="ci"><Icon aria-hidden="true" /></span>
                          <div>
                            <div className="ct">{card.title}</div>
                            <div className="cd">{card.body}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ ROAST MY BAG ═════════════ */}
        <section className="band band-continue">
          <div className="wrap">
            <div className="act reveal">
              <div className="ahead">
                <p className="eyebrow">The fun part</p>
                <h2>Dare the Caddie to roast your bag.</h2>
                <p className="lead">
                  Build your bag, then let the Caddie tell it like it is — the
                  gaps, the vanity clubs, the 3-iron you never hit. Brutal,
                  funny, and built to share.
                </p>
              </div>
              <div className="act-media">
                <img
                  src="/marketing/feat-caddie.jpg"
                  alt=""
                  className="media-cover"
                  loading="lazy"
                />
              </div>
              <div className="cta-row" style={{ marginTop: '28px' }}>
                <Link to={ROAST_CTA} className="btn big">
                  Roast my bag
                  <Flame className="w-[18px] h-[18px]" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="band band-continue">
          <div className="wrap">
            <div className="acts">
              {[FEATURE_GROUPS[2], FEATURE_GROUPS[3]].map((group, idx) => (
                <div className="act reveal" key={group.eyebrow}>
                  <div className="ahead">
                    <p className="eyebrow">{group.eyebrow}</p>
                    <h2>{group.title}</h2>
                  </div>
                  {group.image && (
                    <div className="act-media">
                      <img
                        src={group.image}
                        alt=""
                        className="media-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="caps">
                    {group.cards.map((card) => {
                      const Icon = card.icon;
                      return (
                        <div className="cap" key={card.title}>
                          <span className="ci"><Icon aria-hidden="true" /></span>
                          <div>
                            <div className="ct">{card.title}</div>
                            <div className="cd">{card.body}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {idx === 0 && (
                    <p
                      className="reveal mono"
                      style={{
                        marginTop: '24px',
                        fontSize: '.78rem',
                        letterSpacing: '.06em',
                        color: 'var(--muted)',
                        lineHeight: 1.8,
                      }}
                    >
                      {VERIFICATION_TRUST_CHIPS.join(' · ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ PRICING (golfer matrix) ═════════════
             PricingMatrix keeps its own internal styling (app --color-*
             tokens) and sits on the dark .mkt ground. Anchored #pricing. */}
        <section className="band band-continue">
          <div className="wrap reveal">
            <PricingMatrix
              id="pricing"
              eyebrow="Pricing"
              title="Pick the plan that fits your bag"
              description={
                <>
                  Every plan includes the virtual bag and add-clubs flow. Free
                  includes a limited Caddie chat teaser; Premium unlocks the
                  full toolkit, valuations, and deeper answers; Platinum lifts
                  caps for power users. Two-month free trial on Premium. No card
                  needed. Remaining allowance is always visible in the app.
                </>
              }
              tiers={GOLFER_PRICING_TIERS}
              rows={GOLFER_PRICING_ROWS}
              footnote={
                <>
                  Allowances reset monthly (or yearly on annual plans). Your
                  remaining balance is always shown in the Caddie and
                  Subscriptions screens. Cancel anytime from Subscriptions.
                </>
              }
            />
          </div>
        </section>

        {/* ═════════════ AUDIENCE SPLIT ═════════════ */}
        <section className="band band-continue">
          <div className="wrap">
            <div className="sec-head reveal" style={{ marginBottom: '32px' }}>
              <div>
                <p className="eyebrow">Two ways in</p>
                <h2>Pick your side.</h2>
              </div>
            </div>
            <div className="split">
              {customer ? (
                <button
                  type="button"
                  onClick={goToCore}
                  className="apanel g reveal"
                  style={{ textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
                >
                  <div className="a-bg" aria-hidden="true">
                    <img src="/marketing/panel-golfer.jpg" alt="" className="media-cover" />
                  </div>
                  <span className="aglow" aria-hidden="true" />
                  <span className="atag">For golfers</span>
                  <h3>Know your bag.</h3>
                  <p>Value it, play smarter with an AI caddie, and prove it is yours.</p>
                  <span className="go">
                    Open your bag
                    <ArrowRight aria-hidden="true" />
                  </span>
                </button>
              ) : (
                <Link to={PRIMARY_CTA} className="apanel g reveal">
                  <div className="a-bg" aria-hidden="true">
                    <img src="/marketing/panel-golfer.jpg" alt="" className="media-cover" />
                  </div>
                  <span className="aglow" aria-hidden="true" />
                  <span className="atag">For golfers</span>
                  <h3>Know your bag.</h3>
                  <p>Value it, play smarter with an AI caddie, and prove it is yours.</p>
                  <span className="go">
                    Explore for golfers
                    <ArrowRight aria-hidden="true" />
                  </span>
                </Link>
              )}
              <Link to="/pro" className="apanel p reveal">
                <div className="a-bg" aria-hidden="true">
                  <img src="/marketing/panel-pro.jpg" alt="" className="media-cover" />
                </div>
                <span className="aglow" aria-hidden="true" />
                <span className="atag">For professionals</span>
                <h3>Run the shop.</h3>
                <p>
                  Fitting to order to cash, split supplier orders, return lost
                  member gear, and sell older stock to your members online.
                </p>
                <span className="go">
                  Explore GolfAuth Pro
                  <ArrowRight aria-hidden="true" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* ═════════════ FAQ (FAQ_ITEMS + openFaq) ═════════════
             Same array drives the FAQPage JSON-LD in useHeadMeta above. */}
        <section className="band band-continue" id="faq">
          <div className="wrap">
            <div className="sec-head reveal" style={{ marginBottom: '32px' }}>
              <div>
                <p className="eyebrow">Answers</p>
                <h2>Frequently asked questions.</h2>
              </div>
            </div>
            <div className="reveal" style={{ maxWidth: '820px' }}>
              {FAQ_ITEMS.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={item.q}
                    style={{
                      border: '1px solid var(--hairline)',
                      borderRadius: '14px',
                      marginBottom: '10px',
                      background:
                        'linear-gradient(180deg,rgba(255,255,255,.026),rgba(255,255,255,.006))',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '16px',
                        padding: '18px 20px',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 0,
                        cursor: 'pointer',
                        color: 'var(--ink)',
                        font: 'inherit',
                        fontWeight: 700,
                        fontFamily: 'var(--display)',
                      }}
                    >
                      <span>{item.q}</span>
                      <ChevronDown
                        aria-hidden="true"
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          transition: 'transform .2s',
                          transform: isOpen ? 'rotate(180deg)' : 'none',
                          color: 'var(--cyan)',
                        }}
                      />
                    </button>
                    {isOpen && (
                      <div
                        style={{
                          padding: '0 20px 18px',
                          color: 'var(--body)',
                          lineHeight: 1.6,
                          fontSize: '.95rem',
                        }}
                      >
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═════════════ CLOSING CTA ═════════════ */}
        <section className="close hasbg">
          <div className="bandbg" aria-hidden="true">
            <img src="/marketing/cta-golfer.jpg" alt="" className="media-cover" loading="lazy" />
          </div>
          <div className="wrap reveal">
            <h2>
              Build your digital bag<br />in a few taps.
            </h2>
            <p>
              Real values. AI advice. Verified ownership. Built from real bags
              and real receipts — starting with yours.
            </p>
            <div className="cta-row" style={{ justifyContent: 'center' }}>
              {customer ? (
                <button type="button" className="btn big" onClick={goToCore}>
                  Open your bag
                  <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
                </button>
              ) : (
                <Link to={PRIMARY_CTA} className="btn big">
                  {PRIMARY_CTA_LABEL}
                  <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
                </Link>
              )}
            </div>
            {!customer && (
              <p className="reassure" style={{ marginTop: '18px' }}>
                {CTA_REASSURANCE}
              </p>
            )}
            <StoreBadges align="center" />
          </div>
          <div className="wrap"><div className="rule" /></div>
        </section>

        {/* ═════════════ FOOTER ═════════════ */}
        <footer>
          <div className="wrap row">
            <span>GolfAuth · WITB — every club, known and yours</span>
            <span
              style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}
            >
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/cookies">Cookies</Link>
              <Link to="/delete-account">Delete account</Link>
              <Link to="/contact">Contact</Link>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
